const MIN_QUALITY = 0.3;
const QUALITY_STEP = 0.1;
const START_QUALITY = 0.85;

function loadImageFromFile(file: File): Promise<HTMLImageElement> {
  const objectUrl = URL.createObjectURL(file);
  const image = new Image();

  return new Promise((resolve, reject) => {
    const cleanup = () => {
      URL.revokeObjectURL(objectUrl);
    };

    image.onload = () => {
      cleanup();
      resolve(image);
    };

    image.onerror = () => {
      cleanup();
      reject(new Error("Failed to load image for compression"));
    };

    image.src = objectUrl;
  });
}

function toBlob(canvas: HTMLCanvasElement, quality: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error("Failed to compress image"));
          return;
        }
        resolve(blob);
      },
      "image/jpeg",
      quality,
    );
  });
}

function toJpgName(originalName: string): string {
  const trimmed = originalName.trim();
  const dotIndex = trimmed.lastIndexOf(".");
  const baseName = dotIndex > 0 ? trimmed.slice(0, dotIndex) : trimmed;
  const safeBase = baseName.length > 0 ? baseName : "image";
  return `${safeBase}.jpg`;
}

export async function compressImage(
  file: File,
  maxSizeBytes = 3 * 1024 * 1024,
  maxDimension = 2048,
): Promise<File> {
  if (file.size <= maxSizeBytes) {
    return file;
  }

  const image = await loadImageFromFile(file);
  const largestSide = Math.max(image.width, image.height);
  const scale =
    largestSide > maxDimension && largestSide > 0 ? maxDimension / largestSide : 1;
  const targetWidth = Math.max(1, Math.round(image.width * scale));
  const targetHeight = Math.max(1, Math.round(image.height * scale));

  const canvas = document.createElement("canvas");
  canvas.width = targetWidth;
  canvas.height = targetHeight;

  const ctx = canvas.getContext("2d");
  if (!ctx) {
    throw new Error("Failed to initialize canvas context");
  }

  ctx.drawImage(image, 0, 0, targetWidth, targetHeight);

  let quality = START_QUALITY;
  let bestBlob: Blob | null = null;

  while (quality >= MIN_QUALITY - Number.EPSILON) {
    const blob = await toBlob(canvas, quality);
    if (!bestBlob || blob.size < bestBlob.size) {
      bestBlob = blob;
    }
    if (blob.size <= maxSizeBytes) {
      bestBlob = blob;
      break;
    }
    quality -= QUALITY_STEP;
  }

  if (!bestBlob) {
    throw new Error("Failed to produce compressed image");
  }

  return new File([bestBlob], toJpgName(file.name), {
    type: "image/jpeg",
    lastModified: Date.now(),
  });
}
